/**
 * useStartIntentSeed — the single editor-shell hook that opens a freshly
 * created native diagram WITH content, for any type. Mounts once in
 * App.tsx (which already holds CommandManager + OnboardingContext + doc).
 *
 * Resolves the StartIntent's seed through the SeedRegistry and runs the
 * payload exactly once, only when the canvas is ready (synced + writable):
 *   - `rdm`      → RdmService → ApplyRdmSceneCommand (the production stack).
 *   - `commands` → the provider's imperative builder (cm.execute only).
 *   - `clone`    → no-op (the server cloned the snapshot at create time).
 *
 * Sample/import/agent transport rides StartIntent; template/clone is server.
 *
 * Also owns the activation proof metrics (starter-registry plan §60-second
 * proof): `editor.seeded` after content lands, `editor.first_edit` on the
 * first USER command (seed-generated commands excluded).
 */

import { useEffect, useRef, useState } from 'react';
import type * as Y from 'yjs';
import type { CommandManager } from '../commands/CommandManager';
import type { StartIntent, StartSeed } from './startIntent';
import { runSeedById, applyRdmText } from './runSeed';
import { getSeedProvider } from './SeedRegistry';
import { registerDefaultSeeds } from './registerDefaultSeeds';
import { RDM_CONTENT_IMPORT_HINT, takeRdmOpenContent } from './rdmOpenChannel';
import { Tracker } from '../../domains/analytics/services/Tracker';

type RequestFn = (input: string, init?: RequestInit) => Promise<Response>;

interface EventBusLike {
  on(event: string, handler: (payload: unknown) => void): () => void;
}

export interface UseStartIntentSeedOptions {
  startIntent: StartIntent | null | undefined;
  commandManager: CommandManager;
  /** True only when the doc is synced, access resolved, and the user can write. */
  ready: boolean;
  /** Fires once after content lands, so the shell can zoom-to-fit. */
  onSeeded?: () => void;
  /** Fires when a network-bound `agent` seed (prompt start) fails to
   *  produce content — either the request threw or it returned nothing.
   *  Lets the shell surface a toast instead of failing silently. */
  onSeedError?: (error: unknown, seed: StartSeed) => void;
  /** Yjs doc — lets `commands` seeds write settings/frames (org viewMode,
   *  network containers). Optional; RDM seeds don't need it. */
  doc?: Y.Doc | null;
  /** Diagram type + authenticated fetch — required for `agent` seeds
   *  (prompt start) and for source-backed `sample` seeds such as
   *  `org-from-source`, which hydrate from folder records. */
  diagramType?: string;
  requestFn?: RequestFn;
  /** Folder the diagram lives in — lets source-backed seeds read records. */
  folderId?: string | null;
  /** Editor event bus — enables the `editor.first_edit` proof metric. */
  eventBus?: EventBusLike | null;
  /** Provider dialect hint for command-based seeds such as Blueprint templates. */
  dialectId?: string | null;
}

/** Resolve the starter behind a seed id (lazy — keeps catalog data out of
 *  the editor's initial chunk). Starter ids and seed ids coincide for new
 *  content but differ for the picker cards, so match on the artifact. */
async function starterAttributionForSeed(seedId: string): Promise<Record<string, unknown>> {
  try {
    const [{ listStarters }, { registerDefaultStarters }, { revenueLineOf }] = await Promise.all([
      import('../starters/StarterRegistry'),
      import('../starters/registerDefaultStarters'),
      import('../starters/Starter'),
    ]);
    registerDefaultStarters();
    const starter = listStarters().find(
      (s) => s.artifact.kind === 'seed' && s.artifact.seedId === seedId,
    );
    if (!starter) return {};
    return { starterId: starter.id, revenueLine: revenueLineOf(starter) };
  } catch {
    return {};
  }
}

export interface UseStartIntentSeedResult {
  /** True while a network-bound `agent` seed (prompt start) is generating
   *  content. Drives the editor's "Generating…" overlay. */
  isGeneratingSeed: boolean;
}

export function useStartIntentSeed({
  startIntent,
  commandManager,
  ready,
  onSeeded,
  onSeedError,
  doc,
  diagramType,
  requestFn,
  folderId,
  eventBus,
  dialectId,
}: UseStartIntentSeedOptions): UseStartIntentSeedResult {
  const ranRef = useRef(false);
  const [isGeneratingSeed, setIsGeneratingSeed] = useState(false);
  // first-edit bookkeeping — shared between the seeding effect (which
  // marks the seed-mutation window) and the command listener.
  const seedingInFlightRef = useRef(false);
  const didSeedRef = useRef(false);
  const seedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (ranRef.current || !ready) return;
    const seed = startIntent?.seed;
    if (!seed) return;
    // template/clone = server-side at create; import = panel-derived
    // (for example, OnboardingContext.networkStartPanel) — EXCEPT
    // the rdm-content hint, which is the "Open in Creately" side-channel.
    const isRdmContent = seed.kind === 'import' && seed.hint === RDM_CONTENT_IMPORT_HINT;
    if (seed.kind !== 'sample' && seed.kind !== 'agent' && !isRdmContent) return;
    if (seed.kind === 'agent' && !requestFn) return; // shell didn't wire fetch yet
    if (seed.kind === 'sample') {
      // Source-backed seeds hydrate from folder records; folderId arrives
      // from a separate diagram-info query, so wait for it (the effect
      // re-runs via deps) instead of claiming the one-shot with no context.
      registerDefaultSeeds();
      const provider = getSeedProvider(seed.id);
      if (provider?.needsFolderContext && (!folderId || !requestFn)) return;
    }

    // Claim the run up front so a re-render can't double-seed.
    ranRef.current = true;
    seedingInFlightRef.current = true;
    const isAgentSeed = seed.kind === 'agent';
    if (isAgentSeed) setIsGeneratingSeed(true);

    void (async () => {
      try {
        let seeded = false;
        if (isRdmContent) {
          const rdm = takeRdmOpenContent();
          if (rdm) seeded = await applyRdmText(commandManager, rdm);
        } else if (seed.kind === 'sample') {
          seeded = await runSeedById(commandManager, seed.id, { doc, dialectId, folderId, requestFn });
        } else if (seed.kind === 'agent') {
          const resolvedDiagramType = diagramType ?? startIntent?.type ?? 'flowchart';
          if (resolvedDiagramType === 'org') {
            const { generateOrgChartPromptSeed } = await import('../../domains/orgchart/seeds/orgPromptSeed');
            seeded = await generateOrgChartPromptSeed(
              commandManager,
              seed.prompt,
              requestFn as RequestFn,
              doc,
            );
          } else {
            const { runAgentSeed } = await import('./agentSeed');
            seeded = await runAgentSeed(
              commandManager,
              seed.prompt,
              resolvedDiagramType,
              requestFn as RequestFn,
            );
          }
        }
        if (seeded) {
          didSeedRef.current = true;
          seedIdRef.current =
            seed.kind === 'sample' ? seed.id : isRdmContent ? RDM_CONTENT_IMPORT_HINT : 'agent';
          const attribution =
            seed.kind === 'sample' ? await starterAttributionForSeed(seed.id) : {};
          Tracker.track('editor.seeded', {
            seedId: seedIdRef.current,
            seedKind: seed.kind,
            src: startIntent?.src,
            diagramType: diagramType ?? startIntent?.type,
            ...attribution,
          });
          onSeeded?.();
        } else if (isAgentSeed) {
          // The prompt-start request completed but produced no diagram —
          // surface it instead of leaving a silent empty canvas.
          onSeedError?.(
            new Error('No diagram was generated from this prompt.'),
            seed,
          );
        }
      } catch (error) {
        console.warn('[startIntentSeed] hydration failed', seed, error);
        if (isAgentSeed) onSeedError?.(error, seed);
      } finally {
        seedingInFlightRef.current = false;
        if (isAgentSeed) setIsGeneratingSeed(false);
      }
    })();
  }, [ready, startIntent, commandManager, onSeeded, onSeedError, doc, dialectId, diagramType, requestFn, folderId]);

  // editor.first_edit — the proof metric's second half: the first command
  // the USER executes (seed mutations excluded via the in-flight window).
  const firstEditFiredRef = useRef(false);
  useEffect(() => {
    if (!eventBus || firstEditFiredRef.current) return;
    const openedAt = Date.now();
    const off = eventBus.on('command:executed', () => {
      if (firstEditFiredRef.current || seedingInFlightRef.current) return;
      firstEditFiredRef.current = true;
      Tracker.track('editor.first_edit', {
        sinceOpenMs: Date.now() - openedAt,
        seeded: didSeedRef.current,
        ...(seedIdRef.current ? { seedId: seedIdRef.current } : {}),
        ...(startIntent?.src ? { src: startIntent.src } : {}),
      });
      off?.();
    });
    return () => off?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus]);

  return { isGeneratingSeed };
}
